import { Icon } from "@iconify/react";
import { useState } from "react";
import { toast } from "react-toastify";
import { allowedFileTypes, UploadFileTypes } from "../../constants";
import { useUpload } from "../../hooks";
import { Button, SubmitButton } from "../buttons/Buttons";
import FormWrapper from "../forms/Form";
import ModalWrapper from "./Modal";
import styles from "./Modal.module.css";

function ImageSliderModal({ handleClose, isVisible, handleUpload }) {
  const [imageFiles, setImageFiles] = useState([]);
  const { uploadFile } = useUpload();
  const [fileUploadStatus, setFileUploadStatus] = useState(false);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedUrls, setUploadedUrls] = useState([]);

  const fileType = UploadFileTypes.IMAGES;

  const checkValidFiles = () => {
    const allowed = allowedFileTypes[fileType] || [];
    let notAllowedFiles = [];
    imageFiles.forEach((file) => {
      if (!allowed.includes(file.type)) {
        notAllowedFiles.push(file);
        return;
      }
    });
    if (notAllowedFiles.length > 0) {
      setInvalidFiles(notAllowedFiles);
      toast.error("Invalid file type. The allowed types are jpeg, png");
      return false;
    }
    return true;
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();

    if (imageFiles.length === 0) {
      toast.error("Please select at least one image to upload.");
      return;
    }

    const isValid = checkValidFiles();
    if (!isValid) return;

    setFileUploadStatus(true);

    let uploadError = false;
    let ids = [];
    let urls = [];

    for (const file of imageFiles) {
      const { data, error } = await uploadFile(file, fileType);
      if (!data) {
        uploadError = true;
        break;
      }
      ids.push(data.file_id);
      urls.push(data.url);
    }
    setFileUploadStatus(false);

    if (uploadError) {
      toast.error("Error uploading image");
      return;
    }

    await handleUpload(ids, urls);
    handleClose();
  };

  const handleFileAdd = (e) => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files).filter((file) => !imageFiles.includes(file));
    const allowed = allowedFileTypes[fileType] || [];
    let notAllowedFilesForSize = [];
    let notAllowedFiles = [];
    newFiles.forEach((file) => {
      if (!allowed.includes(file.type)) {
        notAllowedFiles.push(file);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        notAllowedFilesForSize.push(file);
        return;
      }
    });

    if (notAllowedFiles.length > 0) {
      setInvalidFiles(notAllowedFiles);
      toast.error("Invalid file type. The allowed types are jpeg, png");
    }
    if (notAllowedFilesForSize.length > 0) {
      setInvalidFiles(notAllowedFilesForSize);
      toast.error("File size must be less than 10MB.");
    }

    setImageFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  return (
    <ModalWrapper isVisible={isVisible} onClose={() => handleClose()} name="image_upload_modal" level={2} fit>
      <FormWrapper id="image_upload_form" onSubmit={handleImageUpload}>
        <div className="section_title_wrapper">
          <h3 className="section_title">Upload Image Slider</h3>
          <label htmlFor="new_file_header" className={styles.upload_icon_button}>
            <Icon icon="iconoir:add-media-image" inline />
            <input
              type="file"
              id="new_file_header"
              name="new_file_header"
              accept={"image/jpeg, image/png"}
              multiple={true}
              onChange={handleFileAdd}
              hidden
            />
            <span className={styles.add_media_label}>
              Add {fileType === UploadFileTypes.IMAGES ? "Image" : "Video"}
            </span>
          </label>
        </div>
        <div className="column">
          {imageFiles.length > 0 ? (
            <div
              className={
                imageFiles.length > 1 ? (imageFiles.length < 4 ? "row" : styles.image_grid_multi) : styles.image_grid
              }
            >
              {imageFiles.map((file, index) => (
                <div className={styles.uploaded_image_wrapper} key={index}>
                  <img
                    src={URL.createObjectURL(file)}
                    key={index}
                    alt=""
                    className={`${styles.uploaded_image} ${invalidFiles.includes(file) ? styles.invalid_file : ""}`}
                  />
                  <Button
                    onClick={() => setImageFiles((files) => files.filter((f) => f !== file))}
                    icon="ic:outline-delete"
                    iconPosition="start"
                    className="delete_image_button"
                    disabled={fileUploadStatus}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="column center">
              <div className={styles.upload_input_wrapper}>
                <Icon icon="iconoir:add-media-image" className={styles.upload_icon} />
              </div>
              <div>
                <label htmlFor="new_file" className={styles.upload_link}>
                  Browse
                  <input
                    type="file"
                    id="new_file"
                    name="new_file"
                    accept={"image/jpeg, image/png"}
                    multiple={true}
                    onChange={handleFileAdd}
                    hidden
                  />
                </label>
                <span> your files</span>
              </div>
            </div>
          )}
        </div>
        <div className="row center">
          <Button className="ghost_outline_round" onClick={handleClose} disabled={fileUploadStatus}>
            Cancel
          </Button>
          <SubmitButton form="image_upload_form" className="cta" disabled={fileUploadStatus}>
            Upload
          </SubmitButton>
        </div>
      </FormWrapper>
    </ModalWrapper>
  );
}

export default ImageSliderModal;
